import { useEffect, useState } from 'react';
import type { Skill } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';

export function SkillsPage() {
  const { workspaceId } = useApp();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [pending, setPending] = useState<Skill | null>(null);

  async function load() {
    const result = await apiClient.listSkills();
    setSkills(result.items);
  }

  useEffect(() => {
    void load().catch(() => setSkills([]));
  }, [workspaceId]);

  function reset() {
    setEditingId(null);
    setKey('');
    setName('');
    setDescription('');
    setInstructions('');
  }

  function cancel() {
    setShowForm(false);
    reset();
  }

  function toggleForm() {
    if (showForm) cancel();
    else {
      reset();
      setShowForm(true);
    }
  }

  function edit(skill: Skill) {
    setEditingId(skill.id);
    setKey(skill.key);
    setName(skill.name);
    setDescription(skill.description);
    setInstructions(skill.instructions);
    setShowForm(true);
  }

  async function save() {
    if (saving || !key.trim() || !name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await apiClient.updateSkill(editingId, {
          name: name.trim(),
          description: description.trim(),
          instructions,
        });
      } else {
        await apiClient.createSkill({
          key: key.trim(),
          name: name.trim(),
          description: description.trim(),
          instructions,
          examples: [],
        });
      }
      setShowForm(false);
      reset();
      await load();
    } catch {
      /* keep the form open */
    } finally {
      setSaving(false);
    }
  }

  async function remove(skill: Skill) {
    await apiClient.deleteSkill(skill.id);
    setPending(null);
    await load();
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Skills</h1>
          <p className="text-sm text-gray-500">
            Reusable prompt fragments and few-shot examples. Skills are injected into an agent's system prompt to give it a repeatable capability.
          </p>
        </div>
        <Button size="sm" onClick={toggleForm}>New skill</Button>
      </div>

      {showForm && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <h2 className="text-base font-semibold">{editingId ? 'Edit skill' : 'Create skill'}</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="text-xs text-gray-500">
              Key
              <Input className="mt-1" value={key} disabled={!!editingId} placeholder="refund.triage" onChange={(e) => setKey(e.target.value)} />
            </label>
            <label className="text-xs text-gray-500 sm:col-span-2">
              Name
              <Input className="mt-1" value={name} placeholder="Refund triage" onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="text-xs text-gray-500 sm:col-span-3">
              Description
              <Input className="mt-1" value={description} placeholder="What this skill does" onChange={(e) => setDescription(e.target.value)} />
            </label>
            <label className="text-xs text-gray-500 sm:col-span-3">
              Instructions (prompt fragment)
              <textarea
                className="mt-1 h-48 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                value={instructions}
                placeholder="When handling refunds, always..."
                onChange={(e) => setInstructions(e.target.value)}
              />
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>
            <Button size="sm" disabled={!key.trim() || !name.trim() || saving} onClick={() => void save()}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-2">Key</th>
              <th>Name</th>
              <th>Description</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {skills.length === 0 ? (
              <tr><td colSpan={4} className="py-4 text-center text-gray-500">No skills yet.</td></tr>
            ) : skills.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 dark:border-gray-800">
                <td className="py-2"><code className="text-xs">{s.key}</code></td>
                <td>{s.name}</td>
                <td className="max-w-xs truncate text-gray-500">{s.description || '-'}</td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => edit(s)}>Edit</Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setPending(s)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={pending !== null} onOpenChange={(open) => { if (!open) setPending(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete skill</DialogTitle></DialogHeader>
          <DialogBody>
            <p className="text-sm">Delete skill "{pending?.name}"?</p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!pending} onClick={() => pending && void remove(pending)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
